import { useEffect, useRef } from 'react';
import { dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

interface DraggableItem {
  order: number;
}

export const useDragAndDrop = <T extends DraggableItem>(items: T[], handleItems: (newItems: T[]) => void) => {
  // links to DOM elements (wrapper divs) that will be drop targets
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  // ref to save the index of the item being dragged
  const draggedIndexRef = useRef<number | null>(null);

  useEffect(() => {
    const cleanupFunctions: Array<() => void> = [];

    items.forEach((_, index) => {
      const element = refs.current[index];
      if (!element) return;

      const cleanup = dropTargetForElements({
        element,
        getData: () => ({ index }),
        onDragStart: ({ source }) => {
          draggedIndexRef.current = source.data.index as number;
          // update data-is-dragging for all elements
          refs.current.forEach((el, idx) => {
            if (el) {
              el.dataset.isDragging = idx === draggedIndexRef.current ? 'true' : 'false';
            }
          });
        },
        onDrop: () => {
          const draggedIndex = draggedIndexRef.current;
          const targetIndex = index;
          // update order of elements
          if (draggedIndex !== null && draggedIndex !== targetIndex) {
            const newItems = [...items];
            const [draggedItem] = newItems.splice(draggedIndex, 1);
            newItems.splice(targetIndex, 0, draggedItem);
            handleItems(newItems);
          }
          draggedIndexRef.current = null;

          // reset styles (data-attributes) for all elements after drop
          refs.current.forEach((el) => {
            if (el) {
              el.dataset.isDragging = 'false';
              el.dataset.isHover = 'false';
            }
          });
        },
        getDropEffect: () => 'move',
      });

      cleanupFunctions.push(cleanup);
    });

    // monitoring of drag and drop events for all elements
    const monitorCleanup = monitorForElements({
      onDrag: ({ location }) => {
        const targetIndex = location.current.dropTargets[0]?.data?.index as number | undefined;
        // update data-is-hover for all elements
        refs.current.forEach((el, idx) => {
          if (el) {
            el.dataset.isHover = targetIndex !== undefined && idx === targetIndex ? 'true' : 'false';
          }
        });
      },
    });

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
      monitorCleanup();
    };
  }, [items, handleItems]);

  return refs;
};
